// src/services/storeImageService.ts
import { supabase } from '../../lib/supabase';
import { uploadAsync, FileSystemUploadType } from 'expo-file-system/legacy';

const BUCKET = 'store-images';

/**
 * Uploads a local image URI to your Supabase "store-images" bucket.
 * @param uri       local file URI
 * @param vendorId  your vendor identifier
 * @param type      "banner" or "logo"
 * @returns         a public URL to the uploaded file
 */
export async function uploadStoreImage(
    uri: string,
    vendorId: string,
    type: 'banner' | 'logo'
): Promise<string> {
    // figure out extension
    const fileExt = uri.split('.').pop()?.toLowerCase() ?? 'jpg';
    // put it in a vendor-specific folder with a timestamp
    const fileName = `${type}_${Date.now()}.${fileExt}`;
    const filePath = `vendors/${vendorId}/${fileName}`;

    // construct the raw upload endpoint
    const uploadUrl = `${process.env.EXPO_PUBLIC_SUPABASE_URL}/storage/v1/object/${BUCKET}/${filePath}`;

    // grab your auth token
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) {
        throw new Error('No user session—cannot upload.');
    }

    // use FileSystem to PUT the file
    const res = await uploadAsync(uploadUrl, uri, {
        httpMethod: 'PUT',
        uploadType: FileSystemUploadType.BINARY_CONTENT,
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': `image/${fileExt}`,
            'x-upsert': 'true',
        },
    });

    if (res.status !== 200) {
        throw new Error(`Upload failed with status ${res.status}`);
    }

    // construct and return the public URL
    return `${process.env.EXPO_PUBLIC_SUPABASE_URL}/storage/v1/object/public/${BUCKET}/${filePath}`;
}

/**
 * Deletes an image from your "store-images" bucket.
 * @param imageUrl   the full public URL returned by uploadStoreImage()
 */
export async function deleteStoreImage(imageUrl: string): Promise<void> {
    // extract the path after "/store-images/"
    const parts = imageUrl.split(`/${BUCKET}/`);
    const path = parts[1];
    if (!path) {
        console.warn('Could not extract path from URL', imageUrl);
        return;
    }

    const { error } = await supabase.storage
        .from(BUCKET)
        .remove([path]);

    if (error) {
        console.warn('Error removing store image:', error.message);
    }
}